import { paymentOptionSelectionSelectors } from 'ducks/paymentOptionSelection'
import { projectMilestonesSelectors, saveExpectedMilestonePayments } from 'ducks/projectMilestones'
import { PaymentRequestsSystem } from 'projectSections/sections/payments/cashFlowTransactions/utils'
import {
  mergeMilestoneOverridesWithExpected,
  milestoneOverridesNeedHealing,
} from 'projectSections/sections/payments/milestonePaymentOverrideUtils'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { GetExpectedMilestonesResponseType, MilestoneOverrideType, PaymentRequestType } from 'types/cashFlow'
import { ExpectedMilestonePaymentType, PaymentOptionDataType } from 'types/paymentOptions'
import { getExpectedMilestones } from 'util/cashFlow'

type UseExpectedMilestonesForSoldProjectResult = {
  milestones: ExpectedMilestonePaymentType[]
  adHocPaymentRequests: PaymentRequestType[]
  totalPaid: number | undefined
  totalPayable: number | undefined
  isLoading: boolean
  refresh: () => void
}

/**
 * Fetches milestone schedule from the same API as the progress bar for sold projects, for consistency.
 */
export const useExpectedMilestonesForSoldProject = (
  projectId: number | undefined,
  system: PaymentRequestsSystem | undefined,
  paymentOption: PaymentOptionDataType | undefined,
  enabled: boolean
): UseExpectedMilestonesForSoldProjectResult => {
  const [milestones, setMilestones] = useState<ExpectedMilestonePaymentType[]>([])
  const [adHocPaymentRequests, setAdHocPaymentRequests] = useState<PaymentRequestType[]>([])
  const [totalPaid, setTotalPaid] = useState<number | undefined>(undefined)
  const [totalPayable, setTotalPayable] = useState<number | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)

  const dispatch = useDispatch()
  const milestoneRefreshTrigger = useSelector(projectMilestonesSelectors.getRefreshMilestonesTrigger)
  const refreshSystemsTrigger = useSelector(paymentOptionSelectionSelectors.getSystemRefreshTrigger)

  const refresh = useCallback(() => {
    if (!enabled || !projectId || !system?.uuid || !paymentOption?.id || !paymentOption.org_id) {
      return
    }

    const editorSystem = window.editor?.getSystems()?.find((sys) => sys.uuid === system.uuid)
    const editorPaymentOption = editorSystem?.payment_options?.find((pmt) => pmt.id === paymentOption.id)
    const ov = editorSystem?.milestone_payment_overrides || {}
    const milestoneValidationOverridden = ov[`${paymentOption.id}_validation_overridden`] === true ? true : undefined

    // Legacy overrides make the API return milestones the timeline cannot render, so sanitize
    // them here as well as on design load — sold projects must never send raw rows (GS-9851).
    const savedOverrides = editorSystem?.milestone_payment_overrides?.[paymentOption.id]
    const expectedMilestones = editorPaymentOption?.expected_milestone_payments
    const overridesForApi = milestoneOverridesNeedHealing(savedOverrides, expectedMilestones)
      ? mergeMilestoneOverridesWithExpected(savedOverrides as MilestoneOverrideType[], expectedMilestones)
      : savedOverrides

    setIsLoading(true)
    getExpectedMilestones(
      paymentOption.org_id,
      projectId,
      system.uuid,
      paymentOption.id,
      overridesForApi,
      editorPaymentOption?.pricing?.system_price_payable ?? paymentOption.pricing?.system_price_payable,
      milestoneValidationOverridden
    )
      .then((res: GetExpectedMilestonesResponseType) => {
        dispatch(saveExpectedMilestonePayments(res.data.expected_milestone_payments))
        setMilestones(res.data.expected_milestone_payments)
        setAdHocPaymentRequests(res.data.ad_hoc_payment_requests)
        setTotalPaid(res.data.total_amount_paid)
        setTotalPayable(res.data.total_amount_payable)
      })
      .catch(() => {
        setMilestones([])
        setAdHocPaymentRequests([])
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [enabled, projectId, system?.uuid, paymentOption])

  useEffect(() => {
    refresh()
  }, [refresh])

  useEffect(() => {
    if (milestoneRefreshTrigger || refreshSystemsTrigger) {
      refresh()
    }
  }, [milestoneRefreshTrigger, refreshSystemsTrigger, refresh])

  return { milestones, adHocPaymentRequests, totalPaid, totalPayable, isLoading, refresh }
}
